import { Injectable } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '@environments/environment';

export const CHAT_CLAIMS_RETRIED = new HttpContextToken<boolean>(() => false);
export const ADDITIONAL_SIGN_IN_MESSAGE = 'Additional sign-in is required to continue.';

@Injectable()
export class ChatClaimsChallengeInterceptor implements HttpInterceptor {
  constructor(private readonly msalService: MsalService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isChatApiRequest(req.url)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: unknown) => {
        const httpError = error as HttpErrorResponse;
        if (!(httpError instanceof HttpErrorResponse) || httpError.status !== 401) {
          return throwError(() => error);
        }

        if (req.context.get(CHAT_CLAIMS_RETRIED)) {
          return throwError(() => error);
        }

        const challengeHeader =
          httpError.headers?.get('WWW-Authenticate') ?? httpError.headers?.get('www-authenticate');
        const claims = parseClaimsChallenge(challengeHeader);

        if (!claims) {
          return throwError(() => error);
        }

        const correlationId =
          httpError.headers?.get('x-correlation-id') ??
          httpError.headers?.get('x-ms-correlation-id') ??
          httpError.headers?.get('client-request-id') ??
          undefined;

        console.info('[Auth/ChatClaims] challenge-detected', { correlationId });

        const account = this.getPreferredAccount();
        const tokenRequest: PopupRequest = {
          account: account ?? undefined,
          scopes: this.getApiScopes(),
          claims,
        };

        console.info('[Auth/ChatClaims] interaction-started', { correlationId });

        return from(this.msalService.instance.acquireTokenPopup(tokenRequest)).pipe(
          catchError((interactionError: unknown) => {
            if (this.shouldFallbackToRedirect(interactionError)) {
              const redirectRequest: RedirectRequest = {
                account: account ?? undefined,
                scopes: this.getApiScopes(),
                claims,
              };
              void this.msalService.instance.acquireTokenRedirect(redirectRequest);
            }

            console.warn('[Auth/ChatClaims] interaction-failed', { correlationId, error: interactionError });
            return throwError(() => new Error(ADDITIONAL_SIGN_IN_MESSAGE));
          }),
          switchMap((result: { accessToken: string }) => {
            console.info('[Auth/ChatClaims] interaction-succeeded', { correlationId });

            const retryReq = req.clone({
              context: req.context.set(CHAT_CLAIMS_RETRIED, true),
              setHeaders: {
                Authorization: `Bearer ${result.accessToken}`,
              },
            });

            // Retry exactly once with refreshed token; subsequent 401s pass through.
            return next.handle(retryReq);
          })
        );
      })
    );
  }

  private getApiScopes(): string[] {
    return [environment.auth.apiScope];
  }

  private getPreferredAccount(): AccountInfo | null {
    const active = this.msalService.instance.getActiveAccount();
    if (active) {
      return active;
    }

    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      return accounts[0];
    }

    return null;
  }

  private isChatApiRequest(url: string): boolean {
    if (!url) {
      return false;
    }

    const normalized = url.toLowerCase();
    const pathPrefix = '/api/chat';
    const originPrefix = `${window.location.origin.toLowerCase()}${pathPrefix}`;
    const configuredPrefix = `${environment.auth.apiBaseUrl.toLowerCase()}${pathPrefix}`;

    return (
      normalized.startsWith(pathPrefix) ||
      normalized.startsWith(originPrefix) ||
      normalized.startsWith(configuredPrefix)
    );
  }

  private shouldFallbackToRedirect(error: unknown): boolean {
    const code =
      typeof error === 'object' && error != null && 'errorCode' in error
        ? String((error as { errorCode?: unknown }).errorCode ?? '')
        : '';

    return /popup_window_error|popup_window_open_error|empty_window_error|monitor_popup_timeout/i.test(code);
  }
}

export function parseClaimsChallenge(headerValue: string | null): string | null {
  if (!headerValue) {
    return null;
  }

  const normalizedHeader = headerValue.replace(/^\s*Bearer\s+/i, '');
  const params = parseWwwAuthenticateParams(normalizedHeader);
  const errorValue = params.get('error')?.toLowerCase();
  const claimsValue = params.get('claims');

  if (errorValue !== 'insufficient_claims' || !claimsValue) {
    return null;
  }

  try {
    return decodeURIComponent(claimsValue);
  } catch {
    return claimsValue;
  }
}

function parseWwwAuthenticateParams(value: string): Map<string, string> {
  const params = new Map<string, string>();
  let i = 0;

  while (i < value.length) {
    while (i < value.length && /[\s,]/.test(value[i])) {
      i++;
    }

    if (i >= value.length) {
      break;
    }

    const keyStart = i;
    while (i < value.length && /[A-Za-z0-9_-]/.test(value[i])) {
      i++;
    }

    const key = value.slice(keyStart, i).toLowerCase();

    while (i < value.length && /\s/.test(value[i])) {
      i++;
    }

    if (i >= value.length || value[i] !== '=') {
      while (i < value.length && value[i] !== ',') {
        i++;
      }
      continue;
    }

    i++;

    while (i < value.length && /\s/.test(value[i])) {
      i++;
    }

    let parsedValue = '';
    if (i < value.length && value[i] === '"') {
      i++;
      while (i < value.length) {
        const ch = value[i];
        if (ch === '\\' && i + 1 < value.length) {
          parsedValue += value[i + 1];
          i += 2;
          continue;
        }
        if (ch === '"') {
          i++;
          break;
        }
        parsedValue += ch;
        i++;
      }
    } else {
      const valStart = i;
      while (i < value.length && value[i] !== ',') {
        i++;
      }
      parsedValue = value.slice(valStart, i).trim();
    }

    if (key) {
      params.set(key, parsedValue);
    }

    while (i < value.length && value[i] !== ',') {
      i++;
    }
    if (i < value.length && value[i] === ',') {
      i++;
    }
  }

  return params;
}
